/**
 * Telemetria de erros do navegador para o "Diagnóstico do sistema".
 * - Só erros (JS global, promessa rejeitada sem tratamento, ErrorBoundary, falha de rede/5xx no nosso servidor).
 * - Nunca console geral, corpos, cabeçalhos, query strings, tokens, e-mails, CPF, telefone ou cartão.
 * - Deduplicado e limitado por carregamento; envio em lote, sem novas tentativas; falha nunca quebra o app.
 * - Guarda os últimos erros sanitizados neste navegador (buffer pequeno) para consulta após recuperação.
 */

import { categorizeError, safeRoute, isSafeEvent, BUILD_RE, TARGET_RE } from "./safeEvent";

export type DiagKind = "js_error" | "unhandled_rejection" | "react_boundary" | "request_failed" | "chunk_load";
export type DiagEvent = {
  kind: DiagKind;
  app: "site" | "painel" | "outro";
  message: string;
  route: string;
  build: string | null;
  correlation_id: string;
  target?: string;
  http_status?: number;
  at: string;
};

export const LOCAL_BUFFER_KEY = "miahma-diag-recent-v2";
/** Buffer antigo (texto livre sanitizado por regex): removido na leitura. */
export const LEGACY_LOCAL_BUFFER_KEY = "miahma-diag-recent";
export const SEND_TIMEOUT_MS = 5_000;
export const LOCAL_BUFFER_MAX = 20;
export const MAX_EVENTS_PER_LOAD = 20;
export const DEDUP_MS = 10 * 60_000;
export const FLUSH_DELAY_MS = 3_000;
const BATCH_MAX = 10;
const MAX_SEND_FAILURES = 2;

/** Rota segura (lista de rotas do app; segmento desconhecido redigido). */
export const sanitizeRoute = safeRoute;

/** Alvo de uma requisição ao nosso servidor, só por categoria/nome (nunca query). */
export function requestTarget(url: string, backendBase: string): string | null {
  if (!backendBase || !url.startsWith(backendBase)) return null;
  const path = url.slice(backendBase.length).split(/[?#]/)[0];
  let m: RegExpMatchArray | null;
  const safe = (t: string) => (TARGET_RE.test(t) ? t : "other");
  if ((m = path.match(/^\/functions\/v1\/([A-Za-z0-9_-]+)/))) return m[1] === "client-telemetry" ? null : safe(`fn:${m[1]}`);
  if ((m = path.match(/^\/rest\/v1\/rpc\/([A-Za-z0-9_]+)/))) return safe(`rpc:${m[1]}`);
  if ((m = path.match(/^\/rest\/v1\/([A-Za-z0-9_]+)/))) return safe(`rest:${m[1]}`);
  if ((m = path.match(/^\/auth\/v1\/([A-Za-z0-9_]+)/))) return safe(`auth:${m[1]}`);
  if (path.startsWith("/storage/")) return "storage";
  if (path.startsWith("/realtime/")) return null;
  return "other";
}

export function currentBuild(): string | null {
  try {
    const src = Array.from(document.querySelectorAll("script[src]"))
      .map((s) => s.getAttribute("src") || "")
      .map((x) => x.split("/").pop()!.split("?")[0])
      .find((x) => BUILD_RE.test(x) && x !== "dev");
    return src ?? (import.meta.env.DEV ? "dev" : null);
  } catch {
    return null;
  }
}

export function detectApp(loc: { hostname: string; pathname: string } = window.location): DiagEvent["app"] {
  if (loc.hostname.startsWith("painel.") || loc.pathname.startsWith("/admin")) return "painel";
  if (/(^|\.)miahma\.com\.br$/.test(loc.hostname) || loc.hostname.endsWith("lovable.app") || loc.hostname === "localhost") return "site";
  return "outro";
}

const NOISE_RE = /ResizeObserver loop|Script error\.?$|^cancel(l)?ed$|AbortError|The user aborted/i;

type Transport = (events: Omit<DiagEvent, "at">[]) => Promise<boolean>;

export type TelemetryState = {
  sent: Map<string, number>;
  queue: DiagEvent[];
  count: number;
  failures: number;
  disabled: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  transport: Transport | null;
  correlation: string;
  now: () => number;
};

const newId = () => {
  try { return crypto.randomUUID(); } catch { /* sem randomUUID */ }
  try { return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join(""); } catch { /* */ }
  return "00000000000000000000000000000000";
};

export const state: TelemetryState = {
  sent: new Map(), queue: [], count: 0, failures: 0, disabled: false, timer: null, transport: null,
  correlation: newId(), now: () => Date.now(),
};

export function __resetTelemetryForTests(t: Transport | null, now: () => number = () => Date.now()) {
  if (state.timer) clearTimeout(state.timer);
  Object.assign(state, { sent: new Map(), queue: [], count: 0, failures: 0, disabled: false, timer: null, transport: t, correlation: "00000000-0000-4000-8000-000000000000", now });
}

export function readLocalBuffer(): DiagEvent[] {
  try {
    try { localStorage.removeItem(LEGACY_LOCAL_BUFFER_KEY); } catch { /* */ }
    const arr = JSON.parse(localStorage.getItem(LOCAL_BUFFER_KEY) || "[]");
    // Re-valida na leitura: qualquer item fora do formato estrito é descartado.
    return Array.isArray(arr) ? (arr.filter(isSafeEvent) as DiagEvent[]).slice(-LOCAL_BUFFER_MAX) : [];
  } catch { return []; }
}

function pushLocal(e: DiagEvent) {
  try {
    const next = [...readLocalBuffer(), e].slice(-LOCAL_BUFFER_MAX);
    localStorage.setItem(LOCAL_BUFFER_KEY, JSON.stringify(next));
  } catch { /* cota/privado: ignora */ }
}

async function flush() {
  state.timer = null;
  if (state.disabled || !state.transport || !state.queue.length) { state.queue = []; return; }
  const batch = state.queue.splice(0, BATCH_MAX).map(({ at: _at, ...rest }) => rest);
  try {
    const ok = await state.transport(batch);
    if (ok) state.failures = 0;
    else if (++state.failures >= MAX_SEND_FAILURES) state.disabled = true;
  } catch {
    if (++state.failures >= MAX_SEND_FAILURES) state.disabled = true;
  }
  if (state.queue.length && !state.disabled) schedule();
}

function schedule() {
  if (state.timer) return;
  state.timer = setTimeout(() => { void flush(); }, FLUSH_DELAY_MS);
}

function rawText(v: unknown): string {
  try { return typeof v === "string" ? v : v instanceof Error ? `${v.name}: ${v.message}` : String((v as { message?: unknown })?.message ?? ""); } catch { return ""; }
}

/**
 * Registra um erro. Nunca lança. A mensagem original só é usada para classificar
 * (ruído/categoria) e é descartada; request_failed usa "HTTP nnn" ou "network_error".
 */
export function recordDiagnostic(input: { kind: DiagKind; message: unknown; target?: string | null; http_status?: number }) {
  try {
    if (NOISE_RE.test(rawText(input.message))) return;
    const message = input.kind === "request_failed"
      ? (input.http_status && input.http_status >= 100 && input.http_status <= 599 ? `HTTP ${input.http_status}` : "network_error")
      : categorizeError(input.message);
    const kind: DiagKind = input.kind !== "request_failed" && message.endsWith("/chunk_load") ? "chunk_load" : input.kind;
    const route = safeRoute(typeof window !== "undefined" ? window.location.pathname : "/");
    const app = typeof window !== "undefined" ? detectApp() : "outro";
    const target = input.target && TARGET_RE.test(input.target) ? input.target : input.target ? "other" : undefined;
    const fp = [app, kind, message, route, target ?? "", input.http_status ?? ""].join("|");
    const t = state.now();
    const last = state.sent.get(fp);
    if (last !== undefined && t - last < DEDUP_MS) return;
    if (state.count >= MAX_EVENTS_PER_LOAD) return;
    state.sent.set(fp, t);
    state.count++;
    const ev: DiagEvent = {
      kind, app, message, route, build: currentBuild(), correlation_id: state.correlation,
      ...(target ? { target } : {}), ...(Number.isInteger(input.http_status) ? { http_status: input.http_status } : {}),
      at: new Date(t).toISOString(),
    };
    if (!isSafeEvent(ev)) return; // formato estrito ou nada
    pushLocal(ev);
    if (state.disabled) return;
    state.queue.push(ev);
    schedule();
  } catch { /* telemetria nunca quebra o app */ }
}

let installed = false;

/** Instala os capturadores globais uma vez. `backendBase` = URL do servidor. */
export function installClientTelemetry(opts: { backendBase: string; apiKey: string; enabled?: boolean }) {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const endpoint = `${opts.backendBase}/functions/v1/client-telemetry`;
  const nativeFetch = window.fetch.bind(window);
  if (opts.enabled !== false && opts.backendBase && opts.apiKey) {
    state.transport = async (events) => {
      const res = await nativeFetch(endpoint, {
        method: "POST", keepalive: true, credentials: "omit",
        headers: { "Content-Type": "application/json", apikey: opts.apiKey },
        body: JSON.stringify({ events }),
        signal: AbortSignal.timeout(SEND_TIMEOUT_MS),
      });
      return res.ok;
    };
  }

  window.addEventListener("error", (e) => {
    const ev = e as ErrorEvent;
    // Falha de carregamento de recurso (img/script) chega sem message: não é erro de JS.
    if (!ev.message && !ev.error) return;
    recordDiagnostic({ kind: "js_error", message: ev.error ?? ev.message });
  });
  window.addEventListener("unhandledrejection", (e) => {
    recordDiagnostic({ kind: "unhandled_rejection", message: (e as PromiseRejectionEvent).reason });
  });

  // Falhas do NOSSO servidor (rede ou 5xx). 4xx são recusas normais (login, permissão) e ficam fora.
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const target = typeof url === "string" ? requestTarget(url, opts.backendBase) : null;
    if (!target) return nativeFetch(input, init);
    try {
      const res = await nativeFetch(input, init);
      if (res.status >= 500) recordDiagnostic({ kind: "request_failed", message: null, target, http_status: res.status });
      return res;
    } catch (err) {
      const aborted = (err as { name?: string })?.name === "AbortError" || init?.signal?.aborted;
      if (!aborted && navigator.onLine !== false) recordDiagnostic({ kind: "request_failed", message: null, target, http_status: 0 });
      throw err;
    }
  };
}

/** Para ErrorBoundary.componentDidCatch. */
export function reportBoundaryError(error: unknown) {
  recordDiagnostic({ kind: "react_boundary", message: error });
}
